import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api/api.service';
import { API_CONFIG } from '../config/api.config';

export type OfferStatus = 'requested' | 'draft' | 'sent' | 'accepted' | 'rejected' | 'cancelled';

export type OfferUnitSnapshotV1 = {
    title?: string | null;
    unit_number?: string | null;
    type?: string | null;
    floor?: string | number | null;
    bedrooms?: number | null;
    bua?: number | null;
    price?: number | null;
    currency_code?: string | null;
    project_name?: string | null;
    phase_name?: string | null;
};

export type OfferPaymentRowV1 = {
    kind: 'reservation' | 'downpayment' | 'installment' | 'extra';
    label?: string | null;
    due_after_months?: number | null;
    amount?: number | null;
    percent?: number | null;
    installment_no?: number | null;
};

export type CustomPaymentJsonV1 = {
    v: 1;
    price?: number | null;
    currency_code?: string | null;
    rows: OfferPaymentRowV1[];
};

export type OfferRecord = {
    id: string;
    org_id: string | null;
    reservation_id: string | null;
    property_id: string | null;
    project_id: string | null;
    phase_id: string | null;
    payment_plan_id: string | null;
    maintenance_due_date: string | null;
    project_maintenance_due_date?: string | null;
    status: OfferStatus | string | null;
    requested_by_user_id?: string | null;
    sent_by_user_id: string | null;
    cancelled_by_user_id?: string | null;
    decision_by_user_id: string | null;
    public_share_token?: string | null;
    revision_of_offer_id: string | null;
    revision_no: number | null;
    created_by_user_id: string | null;
    sent_at: string | null;
    accepted_at: string | null;
    rejected_at: string | null;
    requested_at?: string | null;
    cancelled_at?: string | null;
    viewed_at?: string | null;
    unit_price: number | null;
    unit_currency_code: string | null;
    pdf_file_id: string | null;
    financial_hash: string | null;
    unit_snapshot_json: OfferUnitSnapshotV1 | any | null;
    custom_payment_json: CustomPaymentJsonV1 | any | null;
    notes: string | null;
    investor_user_id: string | null;
    investor_email: string | null;
    created_at: string | null;
    updated_at: string | null;
};

export type OfferRequestResult = {
    reservation_id: string | null;
    offer_id: string | null;
    status: string | null;
    payment_plan_id: string | null;
    approval_pending: boolean;
    approval_request_id: string | null;
};

export type OfferChecklistItem = {
    key: string;
    label: string;
    ok: boolean;
    optional?: boolean;
    enforced?: boolean;
    skipped?: boolean;
    details?: string | null;
    fix_path?: string | null;
    fix_link?: string | null;
};

export type OfferChecklist = {
    ok: boolean;
    items: OfferChecklistItem[];
};

export type OfferMissingItem = {
    key: string;
    label: string;
    ok?: boolean;
    details?: string | null;
    fix_path?: string | null;
    fix_link?: string | null;
};

export type UnitOfferListItem = {
    property_id: string;
    title: string | null;
    unit_number: string | null;
    project_id: string | null;
    project_name: string | null;
    phase_id: string | null;
    phase_name: string | null;
    updated_at: string | null;
    offer_ready?: boolean | null;
    offer_ready_for_reservation?: boolean | null;
    offer_missing_items?: OfferMissingItem[];
};

export type BulkOfferSendLeadResult = {
    lead_id: string;
    status: 'sent' | 'skipped' | 'error';
    code?: string | null;
    message?: string | null;
};

@Injectable({ providedIn: 'root' })
export class OffersService {
    private api = inject(ApiService);
    private http = inject(HttpClient);

    list(options: {
        page?: number;
        perPage?: number;
        status?: string;
        reservationId?: string;
        propertyId?: string;
    } = {}): Observable<{ items: OfferRecord[]; total: number; page: number; perPage: number }> {
        const page = options.page ?? 1;
        const perPage = options.perPage ?? 25;
        const paramsObj: Record<string, string> = {
            page: String(page),
            per_page: String(perPage)
        };
        if (options.status) paramsObj['status'] = String(options.status);
        if (options.reservationId) paramsObj['reservation_id'] = String(options.reservationId);
        if (options.propertyId) paramsObj['property_id'] = String(options.propertyId);
        const params = new HttpParams({ fromObject: paramsObj });

        return this.api.get<any>('/API2/offers', params).pipe(
            map((resp) => {
                const rows = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.DATA) ? resp.DATA : [];
                const meta = resp?.meta ?? resp?.pagination ?? resp?.PAGINATION ?? resp ?? {};
                const total = Number(meta.total ?? meta.TOTAL ?? meta.total_records ?? meta.totalCount ?? rows.length);
                const safeTotal = Number.isFinite(total) && total >= 0 ? total : rows.length;
                const p = Number(meta.page ?? meta.PAGE ?? page);
                const pp = Number(meta.perPage ?? meta.per_page ?? meta.PER_PAGE ?? perPage);
                return {
                    items: rows.map((r: any) => this.mapOffer(r)),
                    total: safeTotal,
                    page: Number.isFinite(p) && p > 0 ? p : page,
                    perPage: Number.isFinite(pp) && pp > 0 ? pp : perPage
                };
            })
        );
    }

    listUnitOffers(options: { page?: number; perPage?: number; q?: string; projectId?: string; offerReady?: boolean | null } = {}): Observable<{
        items: UnitOfferListItem[];
        total: number;
        page: number;
        perPage: number;
    }> {
        const page = options.page ?? 1;
        const perPage = options.perPage ?? 25;
        const paramsObj: Record<string, string> = {
            page: String(page),
            per_page: String(perPage)
        };
        if (options.q) paramsObj['q'] = String(options.q);
        if (options.projectId) paramsObj['project_id'] = String(options.projectId);
        if (options.offerReady !== undefined && options.offerReady !== null) paramsObj['offer_ready'] = options.offerReady ? 'true' : 'false';
        const params = new HttpParams({ fromObject: paramsObj });

        return this.api.get<any>('/API2/unit-offers', params).pipe(
            map((resp) => {
                const rows = Array.isArray(resp?.data) ? resp.data : [];
                const meta = resp?.meta ?? resp ?? {};
                const total = Number(meta.total ?? meta.total_records ?? rows.length);
                const safeTotal = Number.isFinite(total) && total >= 0 ? total : rows.length;
                const p = Number(meta.page ?? page);
                const pp = Number(meta.per_page ?? meta.perPage ?? perPage);

                return {
                    items: rows.map((r: any) => ({
                        property_id: String(r?.property_id ?? ''),
                        title: r?.title !== null && r?.title !== undefined ? String(r.title) : null,
                        unit_number: r?.unit_number !== null && r?.unit_number !== undefined ? String(r.unit_number) : null,
                        project_id: r?.project_id ? String(r.project_id) : null,
                        project_name: r?.project_name !== null && r?.project_name !== undefined ? String(r.project_name) : null,
                        phase_id: r?.phase_id ? String(r.phase_id) : null,
                        phase_name: r?.phase_name !== null && r?.phase_name !== undefined ? String(r.phase_name) : null,
                        updated_at: r?.updated_at ?? null,
                        offer_ready: r?.offer_ready === undefined || r?.offer_ready === null ? null : Boolean(r.offer_ready),
                        offer_ready_for_reservation:
                            r?.offer_ready_for_reservation === undefined || r?.offer_ready_for_reservation === null
                                ? null
                                : Boolean(r.offer_ready_for_reservation),
                        offer_missing_items: Array.isArray(r?.offer_missing_items)
                            ? r.offer_missing_items.map((item: any) => ({
                                key: String(item?.key || '').trim(),
                                label: String(item?.label || item?.key || '').trim(),
                                ok: item?.ok === undefined ? undefined : Boolean(item.ok),
                                details: item?.details ?? null,
                                fix_path: item?.fix_path ?? null,
                                fix_link: item?.fix_link ?? item?.fix_path ?? null
                            })).filter((item: OfferMissingItem) => Boolean(item.key && item.label))
                            : []
                    })),
                    total: safeTotal,
                    page: Number.isFinite(p) && p > 0 ? p : page,
                    perPage: Number.isFinite(pp) && pp > 0 ? pp : perPage
                };
            })
        );
    }

    get(offerId: string): Observable<OfferRecord> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.get<any>(`/API2/offers/${id}`).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    listByLead(leadId: string): Observable<OfferRecord[]> {
        const id = encodeURIComponent(String(leadId || '').trim());
        return this.api.get<any>(`/API2/leads/${id}/offers`).pipe(
            map((resp) => {
                const rows = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.DATA) ? resp.DATA : [];
                return rows.map((r: any) => this.mapOffer(r));
            })
        );
    }

    createDraft(leadId: string, payload: { payment_plan_id?: string | null; notes?: string | null; custom_payment_json?: any | null; maintenance_due_date?: string | null } = {}): Observable<OfferRecord> {
        const id = encodeURIComponent(String(leadId || '').trim());
        const body: any = {};
        if (payload.payment_plan_id) body.payment_plan_id = payload.payment_plan_id;
        if (payload.notes) body.notes = payload.notes;
        if (payload.custom_payment_json) body.custom_payment_json = payload.custom_payment_json;
        if ('maintenance_due_date' in payload) body.maintenance_due_date = payload.maintenance_due_date;
        return this.api.post<any>(`/API2/leads/${id}/offers`, body).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    updateDraft(offerId: string, patch: { payment_plan_id?: string | null; notes?: string | null; custom_payment_json?: any | null; maintenance_due_date?: string | null } = {}): Observable<OfferRecord> {
        const id = encodeURIComponent(String(offerId || '').trim());
        const body: any = {};
        if ('payment_plan_id' in patch) body.payment_plan_id = patch.payment_plan_id;
        if ('notes' in patch) body.notes = patch.notes;
        if ('custom_payment_json' in patch) body.custom_payment_json = patch.custom_payment_json;
        if ('maintenance_due_date' in patch) body.maintenance_due_date = patch.maintenance_due_date;
        return this.api.patch<any>(`/API2/offers/${id}`, body).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    checklist(offerId: string): Observable<OfferChecklist> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.get<any>(`/API2/offers/${id}/checklist`).pipe(
            map((resp) => {
                const data = resp?.data ?? resp ?? {};
                const items = Array.isArray(data?.items) ? data.items : [];
                return {
                    ok: Boolean(data?.ok),
                    items: items.map((i: any) => ({
                        key: String(i?.key ?? ''),
                        label: String(i?.label ?? i?.key ?? ''),
                        ok: Boolean(i?.ok),
                        optional: Boolean(i?.optional),
                        enforced: i?.enforced === undefined ? undefined : Boolean(i?.enforced),
                        skipped: i?.skipped === undefined ? undefined : Boolean(i?.skipped),
                        details: i?.details ?? null,
                        fix_path: i?.fix_path ?? i?.fix_link ?? null,
                        fix_link: i?.fix_link ?? i?.fix_path ?? null
                    }))
                };
            })
        );
    }

    previewPdf(offerId: string): Observable<Blob> {
        const id = encodeURIComponent(String(offerId || '').trim());
        const url = `${API_CONFIG.API2_URL}/offers/${id}/preview`;
        return this.http.post(url, {}, { responseType: 'blob' });
    }

    previewPropertyPdf(propertyId: string, paymentPlanId?: string | null, recipientEmail?: string | null, maintenanceDueDate?: string | null): Observable<Blob> {
        const id = encodeURIComponent(String(propertyId || '').trim());
        let params = new HttpParams();
        const planId = String(paymentPlanId || '').trim();
        if (planId) params = params.set('payment_plan_id', planId);
        const recipient = String(recipientEmail || '').trim();
        if (recipient) params = params.set('recipient_email', recipient);
        const maintenance = String(maintenanceDueDate || '').trim();
        if (maintenance) params = params.set('maintenance_due_date', maintenance);
        const url = `${API_CONFIG.API2_URL}/properties/${id}/offer.pdf`;
        return this.http.get(url, { params, responseType: 'blob' });
    }

    previewPaymentPlansPdf(propertyId: string, paymentPlanId?: string | null, recipientEmail?: string | null): Observable<Blob> {
        const id = encodeURIComponent(String(propertyId || '').trim());
        let params = new HttpParams();
        const planId = String(paymentPlanId || '').trim();
        if (planId) params = params.set('payment_plan_id', planId);
        const recipient = String(recipientEmail || '').trim();
        if (recipient) params = params.set('recipient_email', recipient);
        const url = `${API_CONFIG.API2_URL}/properties/${id}/payment-plans.pdf`;
        return this.http.get(url, { params, responseType: 'blob' });
    }

    pdf(offerId: string): Observable<Blob> {
        const id = encodeURIComponent(String(offerId || '').trim());
        const url = `${API_CONFIG.API2_URL}/offers/${id}/pdf`;
        return this.http.get(url, { responseType: 'blob' });
    }

    send(offerId: string, options: { skip_checklist_keys?: string[]; send_email?: boolean; recipient_email?: string | null; review_confirmed?: boolean; reviewConfirmed?: boolean; contact_email?: string | null; contact_phone?: string | null; contact_whatsapp?: string | null; contact_website?: string | null } = {}): Observable<OfferRecord> {
        const id = encodeURIComponent(String(offerId || '').trim());
        const body: any = {};
        const skipKeys = Array.isArray(options?.skip_checklist_keys) ? options.skip_checklist_keys : [];
        if (skipKeys.length) body.skip_checklist_keys = skipKeys;
        if (options?.send_email !== undefined) body.send_email = Boolean(options.send_email);
        if (options?.recipient_email) body.recipient_email = String(options.recipient_email);
        if (options?.review_confirmed !== undefined) body.review_confirmed = Boolean(options.review_confirmed);
        if (options?.reviewConfirmed !== undefined) body.reviewConfirmed = Boolean(options.reviewConfirmed);
        // All-payment-plans comparison PDF lets the developer override the
        // contact line printed on the PDF (email/whatsapp/website). Forwarded
        // only when provided so single-plan sends are unaffected.
        if (options?.contact_email) body.contact_email = String(options.contact_email);
        if (options?.contact_phone) body.contact_phone = String(options.contact_phone);
        if (options?.contact_whatsapp) body.contact_whatsapp = String(options.contact_whatsapp);
        if (options?.contact_website) body.contact_website = String(options.contact_website);
        return this.api.post<any>(`/API2/offers/${id}/send`, body).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    duplicate(offerId: string): Observable<OfferRecord> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.post<any>(`/API2/offers/${id}/duplicate`, {}).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    decision(offerId: string, decision: 'accept' | 'reject'): Observable<OfferRecord> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.post<any>(`/API2/offers/${id}/decision`, { decision }).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    accept(offerId: string): Observable<OfferRecord> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.post<any>(`/API2/offers/${id}/accept`, {}).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    cancel(offerId: string): Observable<OfferRecord> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.post<any>(`/API2/offers/${id}/cancel`, {}).pipe(map((resp) => this.mapOffer(resp?.data || resp)));
    }

    deleteOffer(offerId: string): Observable<{ id: string; deleted: boolean }> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.delete<any>(`/API2/offers/${id}`).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => ({
                id: String(data?.id ?? offerId),
                deleted: Boolean(data?.deleted)
            }))
        );
    }

    requestOffer(propertyId: string, payload: { payment_plan_id?: string | null; send_email?: boolean; maintenance_due_date?: string | null } = {}): Observable<OfferRequestResult> {
        const id = encodeURIComponent(String(propertyId || '').trim());
        const body: any = {};
        if (payload.payment_plan_id) body.payment_plan_id = payload.payment_plan_id;
        if (payload.send_email !== undefined) body.send_email = Boolean(payload.send_email);
        if ('maintenance_due_date' in payload) body.maintenance_due_date = payload.maintenance_due_date;
        return this.api.post<any>(`/API2/properties/${id}/request-offer`, body).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => ({
                reservation_id: data?.reservation_id ?? null,
                offer_id: data?.offer_id ?? null,
                status: data?.status ?? null,
                payment_plan_id: data?.payment_plan_id ?? null,
                approval_pending: Boolean(data?.approval_pending ?? data?.approvalPending),
                approval_request_id: data?.approval_request_id ?? data?.approvalRequestId ?? null
            }))
        );
    }

    addAppendixSlide(offerId: string, fileId: string): Observable<any> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.post<any>(`/API2/offers/${id}/appendix-slides`, { file_id: fileId }).pipe(
            map((resp) => resp?.data ?? resp ?? {})
        );
    }

    listAppendixSlides(offerId: string): Observable<Array<{ file_id: string; sort_order: number; url: string }>> {
        const id = encodeURIComponent(String(offerId || '').trim());
        return this.api.get<any>(`/API2/offers/${id}/appendix-slides`).pipe(
            map((resp) => resp?.data ?? []),
            map((slides: any[]) => Array.isArray(slides) ? slides : [])
        );
    }

    deleteAppendixSlide(offerId: string, index: number): Observable<any> {
        const offerIdEncoded = encodeURIComponent(String(offerId || '').trim());
        return this.api.delete<any>(`/API2/offers/${offerIdEncoded}/appendix-slides/${index}`).pipe(
            map((resp) => resp?.data ?? resp ?? {})
        );
    }

    bulkSendByProperty(
        propertyId: string,
        leadIds: string[],
        options: { payment_plan_id: string; skip_checklist_keys?: string[]; review_confirmed?: boolean; reviewConfirmed?: boolean; contact_email?: string | null; contact_phone?: string | null; contact_whatsapp?: string | null; contact_website?: string | null }
    ): Observable<{ sent: number; skipped: number; errors: Array<{ lead_id: string; code?: string | null; message?: string | null }>; results: BulkOfferSendLeadResult[] }> {
        const id = encodeURIComponent(String(propertyId || '').trim());
        const body: any = {
            lead_ids: Array.isArray(leadIds) ? leadIds : [],
            payment_plan_id: String(options?.payment_plan_id || '').trim()
        };
        const skipKeys = Array.isArray(options?.skip_checklist_keys) ? options.skip_checklist_keys : [];
        if (skipKeys.length) body.skip_checklist_keys = skipKeys;
        if (options?.review_confirmed !== undefined) body.review_confirmed = Boolean(options.review_confirmed);
        if (options?.reviewConfirmed !== undefined) body.reviewConfirmed = Boolean(options.reviewConfirmed);
        // Comparison-PDF contact override (all-payment-plans only).
        if (options?.contact_email) body.contact_email = String(options.contact_email);
        if (options?.contact_phone) body.contact_phone = String(options.contact_phone);
        if (options?.contact_whatsapp) body.contact_whatsapp = String(options.contact_whatsapp);
        if (options?.contact_website) body.contact_website = String(options.contact_website);
        return this.api.post<any>(`/API2/properties/${id}/offers/send`, body).pipe(
            map((resp) => resp?.data || resp || {}),
            map((data) => ({
                sent: Number(data?.sent ?? 0) || 0,
                skipped: Number(data?.skipped ?? 0) || 0,
                errors: Array.isArray(data?.errors)
                    ? data.errors.map((e: any) => ({
                        lead_id: String(e?.lead_id ?? ''),
                        code: e?.code ?? null,
                        message: e?.message ?? null
                    }))
                    : [],
                results: Array.isArray(data?.results)
                    ? data.results.map((entry: any) => ({
                        lead_id: String(entry?.lead_id ?? ''),
                        status: String(entry?.status ?? 'error') as BulkOfferSendLeadResult['status'],
                        code: entry?.code ?? null,
                        message: entry?.message ?? null
                    }))
                    : []
            }))
        );
    }

    private mapOffer(row: any): OfferRecord {
        const safe = row || {};
        return {
            id: String(safe.id ?? safe.ROWID ?? ''),
            org_id: safe.org_id ?? null,
            reservation_id: safe.reservation_id ?? null,
            property_id: safe.property_id ?? null,
            project_id: safe.project_id ?? null,
            phase_id: safe.phase_id ?? null,
            payment_plan_id: safe.payment_plan_id ?? null,
            maintenance_due_date: safe.maintenance_due_date ? String(safe.maintenance_due_date).slice(0, 10) : null,
            project_maintenance_due_date: safe.project_maintenance_due_date ? String(safe.project_maintenance_due_date).slice(0, 10) : null,
            status: safe.status ?? null,
            requested_by_user_id: safe.requested_by_user_id ?? null,
            public_share_token: safe.public_share_token ?? safe.publicShareToken ?? null,
            revision_of_offer_id: safe.revision_of_offer_id ?? null,
            revision_no: safe.revision_no !== null && safe.revision_no !== undefined ? Number(safe.revision_no) : null,
            created_by_user_id: safe.created_by_user_id ?? null,
            sent_by_user_id: safe.sent_by_user_id ?? null,
            cancelled_by_user_id: safe.cancelled_by_user_id ?? null,
            decision_by_user_id: safe.decision_by_user_id ?? null,
            sent_at: safe.sent_at ?? null,
            accepted_at: safe.accepted_at ?? null,
            rejected_at: safe.rejected_at ?? null,
            requested_at: safe.requested_at ?? null,
            cancelled_at: safe.cancelled_at ?? null,
            viewed_at: safe.viewed_at ?? null,
            unit_price: safe.unit_price !== null && safe.unit_price !== undefined ? Number(safe.unit_price) : null,
            unit_currency_code: safe.unit_currency_code ?? null,
            pdf_file_id: safe.pdf_file_id ?? null,
            financial_hash: safe.financial_hash ?? null,
            unit_snapshot_json: safe.unit_snapshot_json ?? null,
            custom_payment_json: safe.custom_payment_json ?? null,
            notes: safe.notes ?? null,
            investor_user_id: safe.investor_user_id ?? null,
            investor_email: safe.investor_email ?? null,
            created_at: safe.created_at ?? null,
            updated_at: safe.updated_at ?? null
        };
    }
}
